import { effect, Injectable, Injector, Signal, signal } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { RoutesConfigService } from '../../config/routes-config.service';
import { AuthEventService } from '../auth-event.service';
import { AuthService } from '../auth.service';
import { DataService } from '../data.service';
import { DeviceSessionService } from '../device-session.service';
import { ErrorHandlingService } from '../error-handling.service';
import { GapSettings } from '../../config/gapsettings';
import { StorageService } from '../storage.service';
import { PathsService } from '../state/paths.service';
import { UserService } from '../user.service';
import { DeviceService } from '../../utils/device.service';

@Injectable({
  providedIn: 'root'
})
export class AppFlowService {
  private initialized = false;
  private routerSubscription?: Subscription;
  private authSubscriptionsInitialized = false;
  private mqaFetchTimer: ReturnType<typeof setTimeout> | null = null;
  private reactiveRouteEffectsInitialized = false;
  private readonly routePathState = signal<string>('/');
  private readonly routePathSubject = new BehaviorSubject<string>('/');

  readonly routePath: Signal<string> = this.routePathState.asReadonly();
  readonly routePath$ = this.routePathSubject.asObservable();

  constructor(
    private router: Router,
    private data: DataService,
    private authEventService: AuthEventService,
    // PAUSED: auth-timing service removed — see ticket
    // private authTimingService: AuthTimingService,
    private deviceSessionService: DeviceSessionService,
    private errorService: ErrorHandlingService,
    private gapSettings: GapSettings,
    private injector: Injector,
    private storageService: StorageService,
    private pathsService: PathsService,
    private authService: AuthService,
    private routesConfig: RoutesConfigService,
    private userService: UserService,
    private deviceService: DeviceService,
  ) {}

  /**
   * Initializes the AppFlowService by setting up the startup context,
   * auth subscriptions, reactive route effects, and router event handling.
   * This is the main entry point that must be called once at application bootstrap.
   * @returns {Promise<void>} Resolves when initialization is complete.
   */
  async init(): Promise<void> {
    if (this.initialized) {
      return;
    }

    await this.initializeStartupContext();
    this.initializeAuthSubscriptions();
    this.initializeReactiveRouteEffects();

    this.routerSubscription = this.router.events.subscribe(async (event) => {
      if (event instanceof NavigationStart) {
        this.handleErrorOnNavigationStart(event.url);
        await this.data.handleNavigationStart(event.url);
      }

      if (event instanceof NavigationEnd) {
        await this.data.handleNavigationEnd(event.url);
        await this.handleNavigationEnd(event.url);
      }
    });

    this.initialized = true;
    await this.handleInitialRoute();
  }

  private async initializeStartupContext(): Promise<void> {
    try {
      const deviceInfo = await this.deviceService.init();

      await this.storageService.preloadDexieCache([
        'deviceId',
        'device_fingerprint',
        'deviceInfo',
        'authToken',
        'userData'
      ]);

      await this.data.initializeDeviceContext(deviceInfo);
      await this.data.loadLetters();
    } catch (error) {
      console.error('AppFlowService: Failed to initialize startup context', error);
    }
  }

  private initializeAuthSubscriptions(): void {
    if (this.authSubscriptionsInitialized) {
      return;
    }

    this.authEventService.authEvent$.subscribe(event => {
      switch (event.type) {
        case 'clear_auth':
          if (!this.data.token) {
            console.log('Auth event received: clear_auth');
            // PAUSED: auth-timing service removed — see ticket
            // this.authTimingService.reset();
            this.userService.initiateSessionExpired(false, { show: false });
          }
          break;

        case 'token_expired':
        case 'forbidden_access':
        case 'session_expired':
        case 'inactivity_logout':
        case 'mfa_logout':
          void this.storageService.clearToken();
          console.log('Auth event received:', event?.type, event?.reason);
          // PAUSED: auth-timing service removed — see ticket
          // this.authTimingService.reset();
          this.userService.initiateSessionExpired();
          break;
      }
    });

    if (this.gapSettings.isSessionTimeoutEnabled()) {
      this.userService.isLoggedIn$.subscribe(isLoggedIn => {
        if (isLoggedIn && this.gapSettings.enable_session_timeout === '1') {
          console.log('User logged in, starting device session monitoring');
          // PAUSED: auth-timing service removed — see ticket
          // this.authTimingService.setLoginState(true);
          // this.authTimingService.startTimingEvents();
          // this.authTimingService.initialize();
        }
      });

      this.deviceSessionService.sessionStatus$.subscribe(status => {
        if (!status.isValid && status.error) {
          console.log('Device session validation failed:', status.error);
          switch (status.error) {
            case 'token_expired':
              this.authEventService.emitTokenExpired('Device session token expired');
              break;
            case 'session_expired':
              this.authEventService.emitSessionExpired('Device session expired');
              break;
            case 'inactivity_timeout':
              this.authEventService.emitInactivityLogout('Device session inactivity timeout');
              break;
            default:
              console.warn('Device session error (not auth-related):', status.error);
              break;
          }
        }
      });
    }

    this.authSubscriptionsInitialized = true;
  }

  private initializeReactiveRouteEffects(): void {
    if (this.reactiveRouteEffectsInitialized) {
      return;
    }

    effect(() => {
      const routePath = this.routePath();

      if (this.mqaFetchTimer) {
        clearTimeout(this.mqaFetchTimer);
        this.mqaFetchTimer = null;
      }

      if (routePath === '/home') {
        this.mqaFetchTimer = setTimeout(async () => {
          await this.data.fetchMqaQuestionnaire();
        }, 2000);
      }
    }, { injector: this.injector });

    this.reactiveRouteEffectsInitialized = true;
  }

  /**
   * Handles navigation end events.
   * @param url The URL of the navigation end event.

   */
  private async handleNavigationEnd(url: string): Promise<void> {
    const normalizedUrl = this.normalizeUrl(url);

    this.routePathState.set(normalizedUrl);
    this.routePathSubject.next(normalizedUrl);

    if (['/login', '/guest'].includes(normalizedUrl)) {
      if (!!this.data.token && this.authService.isMfaValidated()) {
        this.router.navigateByUrl('/fakesplash', { skipLocationChange: true });
        return;
      }

      this.authEventService.emitClearAuth();
      // PAUSED: auth-timing service removed — see ticket
      // this.authTimingService.reset();
    }

    const isPublicRoute = this.routesConfig.isPublicRoute(normalizedUrl);
    const isMfaValidated = this.authService.isMfaValidated();
    const isLoggedIn = this.authService.isLoggedIn();
    const canAccessProtectedRoute = this.authService.canAccessProtectedRoute();

    console.log('[MFA Guard]', {
      url: normalizedUrl,
      isPublicRoute,
      canAccessProtectedRoute,
      isMfaValidated,
      isLoggedIn,
      enable_login_mfa_ls: localStorage.getItem('enable_login_mfa'),
      mfaAuthValidated_ls: localStorage.getItem('mfaAuthValidated'),
      accountValidated: this.data.user?.account_validated,
      demoAccount: this.data.user?.demo_account,
    });

    if (!isPublicRoute && !canAccessProtectedRoute) {
      console.log('[MFA Guard] Logging out - protected route without verified MFA');
      await this.data.logout(true, true, '', true);
      return;
    }

    await this.storeVisitedUrl(normalizedUrl);
  }

  private handleErrorOnNavigationStart(url: string): void {
    const currentError = this.errorService.getCurrentError();
    if (!currentError) {
      return;
    }

    const targetPath = this.normalizeUrl(url);
    const errorTargetPath = currentError.targetPage;

    if (targetPath !== errorTargetPath) {
      console.log('AppFlowService: Clearing error - navigating away from error page:', {
        errorCode: currentError.code,
        errorTarget: errorTargetPath,
        navigatingTo: targetPath
      });
      this.errorService.clearError();
      return;
    }

    console.log('AppFlowService: Keeping error - navigating TO error target page:', {
      errorCode: currentError.code,
      targetPage: errorTargetPath
    });
  }

  private async storeVisitedUrl(url: string): Promise<void> {
    if (url.includes('need-help')) {
      return;
    }

    try {
      const lastVisitedUrl = await this.pathsService.getLastVisitedUrl();
      console.log('[Router] Last visited URL from PathsService:', lastVisitedUrl, 'Current URL:', url);

      if (lastVisitedUrl !== url) {
        console.log('[Router] Storing new URL:', url);
        await this.pathsService.addPath(url);
        localStorage.setItem('lastVisitedUrl', url);
      }
    } catch (error) {
      console.error('[Router] Error getting last visited URL:', error);
      await this.pathsService.addPath(url);
      localStorage.setItem('lastVisitedUrl', url);
    }
  }

  private async handleInitialRoute(): Promise<void> {
    // TEMP: on deep links router.url is still '/' at bootstrap, so fall back to
    // window.location.pathname to get the actual target route (example webform testing without login)
    const bootUrl = this.router.url && this.router.url !== '/' ? this.router.url : window.location.pathname;
    const initialPathname = this.normalizeUrl(bootUrl);
    this.routePathState.set(initialPathname || '/');
    this.routePathSubject.next(initialPathname || '/');

    const isPublicRoute = this.routesConfig.isPublicRoute(initialPathname);
    const isMfaValidated = this.authService.isMfaValidated();
    const isLoggedIn = this.authService.isLoggedIn();
    const canAccessProtectedRoute = this.authService.canAccessProtectedRoute();

    console.log('[MFA Guard] Post-subscribe check:', {
      url: initialPathname,
      isPublicRoute,
      canAccessProtectedRoute,
      isMfaRequired: this.authService.isMfaRequired(),
      isMfaValidated,
      isLoggedIn,
      enable_login_mfa_ls: localStorage.getItem('enable_login_mfa'),
      mfaAuthValidated_ls: localStorage.getItem('mfaAuthValidated'),
      isUserLogged: localStorage.getItem('isUserLogged'),
    });

    if (initialPathname && !isPublicRoute && !canAccessProtectedRoute) {
      console.log('[MFA Guard] Initial load logout from protected route:', initialPathname);
      await this.data.logout(true, true, '', true);
    }
  }

  private normalizeUrl(url: string): string {
    return (url || '').split('?')[0].split('#')[0].toLowerCase();
  }
}